import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    ReactNode
} from 'react';
import {signalRService} from '../services/signalr';
import {useAuthStateManager} from '../hooks/useAuthStateManager';

interface SignalRContextType {
    isConnected: boolean;
    service: typeof signalRService;
}

const SignalRContext = createContext<SignalRContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useSignalR = () => {
    const context = useContext(SignalRContext);
    if (!context) {
        throw new Error('useSignalR must be used within a SignalRProvider');
    }
    return context;
};

interface SignalRProviderProps {
    children: ReactNode;
}

export const SignalRProvider: React.FC<SignalRProviderProps> = ({children}) => {
    const [isConnected, setIsConnected] = useState(false);
    const {isAuthenticated, customerId, getAccessToken} = useAuthStateManager();

    useEffect(() => {
        const initSignalR = async () => {
            // Only connect if authenticated
            if (!isAuthenticated) {
                return;
            }

            // Pass the getAccessToken function from auth state manager
            await signalRService.startConnection(getAccessToken);

            if (customerId) {
                await signalRService.joinGroup(customerId);
            }

            setIsConnected(signalRService.getConnected());
        };

        initSignalR();

        // Cleanup on unmount or logout
        // Keep connection alive during navigation
    }, [isAuthenticated, customerId, getAccessToken]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            signalRService.stopConnection();
        };
    }, []);

    return (
        <SignalRContext.Provider value={{isConnected, service: signalRService}}>
            {children}
        </SignalRContext.Provider>
    );
};
